import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { colors as C, fonts as F } from '../theme'
import { useIsMobile } from '../hooks/useIsMobile'

const h2Style = {
  fontSize: '18px',
  fontWeight: 500,
  color: C.textPrimary,
  marginBottom: '1rem',
}

const h3Style = {
  fontSize: '15px',
  fontWeight: 600,
  color: C.textPrimary,
  marginTop: '1.2rem',
  marginBottom: '0.6rem',
}

const pGap = { marginTop: '0.8rem' }
const sectionStyle = { marginBottom: '2.5rem' }
const ulStyle = { marginLeft: '1.5rem', marginTop: '0.8rem' }

const ExtLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: C.accent, textDecoration: 'underline' }}>{children}</a>
)

export default function PrivacyPage() {
  const isMobile = useIsMobile()

  const cellStyle = {
    padding: '10px 12px',
    borderBottom: `0.5px solid ${C.border}`,
    fontSize: '0.82rem',
    lineHeight: 1.5,
    verticalAlign: 'top',
  }
  const thStyle = { ...cellStyle, fontWeight: 600, color: C.textPrimary, background: C.bgPage }

  return (
    <div style={{ background: C.bgPage, minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: C.bgCard,
        borderBottom: `0.5px solid ${C.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: C.textPrimary,
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}>
            <ChevronLeft size={20} />
            Retour
          </Link>
          <div style={{
            fontSize: '1.2rem',
            fontFamily: F.serif,
            fontWeight: 400,
            color: C.textPrimary,
          }}>
            IAvocat<span style={{ fontStyle: 'italic', color: C.accent }}>.ai</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div style={{
        maxWidth: '760px',
        margin: '0 auto',
        padding: isMobile ? '32px 18px' : '60px 24px',
        background: C.bgCard,
        minHeight: '100vh',
      }}>
        <h1 style={{
          fontFamily: F.serif,
          fontSize: isMobile ? '28px' : '36px',
          fontWeight: 400,
          color: C.textPrimary,
          marginBottom: '0.5rem',
        }}>
          Politique de Confidentialit&eacute;
        </h1>
        <p style={{
          fontFamily: F.sans,
          fontSize: '12px',
          fontStyle: 'italic',
          color: C.textSecondary,
          marginBottom: '0.3rem',
        }}>
          Derni&egrave;re mise &agrave; jour : 20 avril 2026 &mdash; Version 2.1
        </p>
        <p style={{
          fontFamily: F.sans,
          fontSize: '12px',
          fontStyle: 'italic',
          color: C.textSecondary,
          marginBottom: '2rem',
        }}>
          La pr&eacute;sente politique d&eacute;crit comment IAvocat.ai collecte, utilise, stocke et prot&egrave;ge vos donn&eacute;es personnelles conform&eacute;ment au R&egrave;glement (UE) 2016/679 (RGPD).
        </p>

        <div style={{ fontFamily: F.sans, lineHeight: 1.8, color: C.textSecondary }}>

          {/* 1. Responsable de traitement */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>1. Identit&eacute; du responsable de traitement</h2>
            <p>Le responsable de traitement des donn&eacute;es collect&eacute;es via la plateforme IAvocat.ai est :</p>
            <ul style={ulStyle}>
              <li><strong>Raison sociale :</strong> MY LEGAL ACT INFORMATION TECHNOLOGY CONSULTANTS - FZCO (ci-apr&egrave;s &laquo;&nbsp;My Legal Act&nbsp;&raquo;)</li>
              <li><strong>Forme juridique :</strong> Free Zone Company (FZCO) de droit &eacute;mirien</li>
              <li><strong>Licence :</strong> n&deg; 66207, d&eacute;livr&eacute;e le 11 juillet 2025 par la Dubai Integrated Economic Zones Authority (DIEZ), au sein de l'International Free Zone Authority (IFZA)</li>
              <li><strong>Enregistrement :</strong> n&deg; 64173</li>
              <li><strong>Si&egrave;ge social :</strong> IFZA Properties, DSO-IFZA, Dubai Silicon Oasis, Dubai, &Eacute;mirats Arabes Unis</li>
              <li><strong>TRN (VAT) :</strong> 105094023600001</li>
              <li><strong>Contact RGPD :</strong> <ExtLink href="mailto:privacy@iavocat.ai">privacy@iavocat.ai</ExtLink></li>
              <li><strong>Site :</strong> <ExtLink href="https://iavocat.ai">https://iavocat.ai</ExtLink></li>
            </ul>
          </section>

          {/* 1bis. Représentant UE (art. 27 RGPD) */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>1 bis. Repr&eacute;sentant dans l'Union europ&eacute;enne (art. 27 RGPD)</h2>
            <p>
              Conform&eacute;ment &agrave; l'article 27 du RGPD, My Legal Act &eacute;tant &eacute;tablie hors de l'Union europ&eacute;enne et traitant des donn&eacute;es de personnes situ&eacute;es dans l'UE, un repr&eacute;sentant &eacute;tabli dans l'Union est en cours de d&eacute;signation. Ses coordonn&eacute;es seront publi&eacute;es sur la pr&eacute;sente page d&egrave;s sa d&eacute;signation :
            </p>
            <ul style={ulStyle}>
              <li><strong>Repr&eacute;sentant UE :</strong> prestataire de repr&eacute;sentation en cours de d&eacute;signation</li>
              <li><strong>Contact (dans l'intervalle) :</strong> <ExtLink href="mailto:privacy@iavocat.ai">privacy@iavocat.ai</ExtLink></li>
            </ul>
            <p style={pGap}>
              Toute personne concern&eacute;e peut s'adresser indiff&eacute;remment au responsable de traitement ou &agrave; son repr&eacute;sentant UE pour exercer ses droits.
            </p>
          </section>

          {/* 2. Données traitées */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>2. Donn&eacute;es trait&eacute;es</h2>

            <h3 style={h3Style}>2.1 Donn&eacute;es d'identification de l'avocat utilisateur</h3>
            <p>Nom, pr&eacute;nom, email professionnel, t&eacute;l&eacute;phone professionnel, barreau d'inscription, num&eacute;ro de toque, adresse du cabinet.</p>

            <h3 style={h3Style}>2.2 Donn&eacute;es d'authentification</h3>
            <p>Email et hash du mot de passe (le mot de passe n'est jamais stock&eacute; en clair), tokens OAuth Google et Microsoft.</p>

            <h3 style={h3Style}>2.3 Donn&eacute;es de facturation</h3>
            <p>Identifiant client Stripe, historique des paiements. Les coordonn&eacute;es bancaires ne transitent jamais par nos serveurs &mdash; le traitement est r&eacute;alis&eacute; directement par Stripe.</p>

            <h3 style={h3Style}>2.4 Contenu professionnel de l'avocat</h3>
            <ul style={ulStyle}>
              <li>Documents de dossiers d&eacute;pos&eacute;s ou synchronis&eacute;s depuis OneDrive / Google Drive</li>
              <li>Emails professionnels synchronis&eacute;s (Gmail / Outlook)</li>
              <li>&Eacute;v&eacute;nements de calendrier (Google Calendar / Microsoft Calendar)</li>
              <li>Conversations avec les agents IA</li>
              <li>Notes, brouillons d'actes, requ&ecirc;tes</li>
            </ul>

            <h3 style={h3Style}>2.5 Donn&eacute;es indirectes (donn&eacute;es de tiers)</h3>
            <p>
              Les dossiers de l'avocat peuvent contenir des noms et donn&eacute;es de ses clients, des parties adverses, t&eacute;moins et magistrats. Ces donn&eacute;es sont couvertes par le secret professionnel de l'avocat (art. 66-5 de la loi du 31 d&eacute;cembre 1971).
            </p>

            <h3 style={h3Style}>2.6 Donn&eacute;es techniques</h3>
            <p>Logs d'acc&egrave;s, adresse IP, user-agent, m&eacute;triques d'usage anonymis&eacute;es.</p>

            <h3 style={h3Style}>2.7 Cat&eacute;gories particuli&egrave;res de donn&eacute;es (art. 9 et 10 RGPD)</h3>
            <p>
              Les dossiers d'avocats p&eacute;nalistes ou sp&eacute;cialis&eacute;s en droit de la sant&eacute; peuvent contenir des donn&eacute;es relevant de l'article 9 du RGPD (donn&eacute;es de sant&eacute;, opinions politiques, origine ethnique) ou de l'article 10 (condamnations p&eacute;nales et infractions). Le traitement de ces donn&eacute;es est fond&eacute; sur l'article 9.2.f du RGPD (constatation, exercice ou d&eacute;fense d'un droit en justice) et sur la n&eacute;cessit&eacute; du traitement par l'avocat dans le cadre de sa mission professionnelle.
            </p>
          </section>

          {/* 4. Bases juridiques */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>3. Bases juridiques du traitement</h2>
            <ul style={ulStyle}>
              <li><strong>Ex&eacute;cution du contrat (art. 6.1.b RGPD) :</strong> fourniture du service IAvocat.ai, gestion du compte et de l'abonnement, g&eacute;n&eacute;ration de documents et analyses via IA, synchronisation des outils professionnels.</li>
              <li><strong>Obligation l&eacute;gale (art. 6.1.c RGPD) :</strong> conservation comptable, obligations fiscales, lutte anti-fraude.</li>
              <li><strong>Int&eacute;r&ecirc;t l&eacute;gitime (art. 6.1.f RGPD) :</strong> s&eacute;curit&eacute; de la plateforme, pr&eacute;vention de la fraude, am&eacute;lioration du service, support client.</li>
              <li><strong>Consentement (art. 6.1.a RGPD) :</strong> newsletter et communications marketing, le cas &eacute;ch&eacute;ant. Vous pouvez retirer votre consentement &agrave; tout moment.</li>
              <li><strong>Pour les donn&eacute;es art. 9 :</strong> exercice ou d&eacute;fense d'un droit en justice (art. 9.2.f RGPD).</li>
              <li><strong>Pour les donn&eacute;es art. 10 :</strong> n&eacute;cessit&eacute; du traitement par un avocat dans le cadre de sa mission professionnelle.</li>
            </ul>
          </section>

          {/* 5. Finalités */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>4. Finalit&eacute;s du traitement</h2>
            <ul style={ulStyle}>
              <li>Permettre &agrave; l'avocat d'organiser et d'exploiter ses dossiers clients</li>
              <li>G&eacute;n&eacute;rer des analyses, synth&egrave;ses et projets d'actes via intelligence artificielle</li>
              <li>Synchroniser les outils professionnels de l'avocat (email, agenda, drive)</li>
              <li>Facturation et gestion du compte utilisateur</li>
              <li>S&eacute;curit&eacute; de la plateforme et pr&eacute;vention de la fraude</li>
              <li>Support client et am&eacute;lioration continue du service</li>
            </ul>
          </section>

          {/* 6. Sous-traitants */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>5. Sous-traitants (art. 28 RGPD)</h2>
            <p>
              Conform&eacute;ment &agrave; l'article 28 du RGPD, nous faisons appel aux sous-traitants suivants pour le traitement de vos donn&eacute;es. Les sous-traitants h&eacute;berg&eacute;s ou &eacute;tablis dans l'Union europ&eacute;enne sont li&eacute;s par un accord de traitement des donn&eacute;es (DPA). Les voies de traitement hors UE sont d&eacute;crites dans la colonne &laquo;&nbsp;Transfert hors UE&nbsp;&raquo; ci-dessous et, pour la voie exceptionnelle concernant Anthropic, d&eacute;taill&eacute;es &agrave; la section 7.
            </p>
            <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: `0.5px solid ${C.border}`, fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Sous-traitant</th>
                    <th style={thStyle}>Finalit&eacute;</th>
                    <th style={thStyle}>Localisation</th>
                    <th style={thStyle}>Transfert hors UE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={cellStyle}><strong>Vercel Inc.</strong> (USA)</td>
                    <td style={cellStyle}>H&eacute;bergement frontend, fonctions serverless</td>
                    <td style={cellStyle}>R&eacute;gions EU (Frankfurt, Paris)</td>
                    <td style={cellStyle}>DPA + CCT (Clauses Contractuelles Types Commission UE 2021)</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>Supabase Inc.</strong> (USA)</td>
                    <td style={cellStyle}>Base de donn&eacute;es, authentification, stockage fichiers</td>
                    <td style={cellStyle}>UE &mdash; Irlande (eu-west-1) exclusivement</td>
                    <td style={cellStyle}>DPA + CCT</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>AWS Bedrock</strong> (Amazon Web Services EMEA SARL, Luxembourg)</td>
                    <td style={cellStyle}>Inf&eacute;rence IA (mod&egrave;les Claude)</td>
                    <td style={cellStyle}>eu-central-1 (Frankfurt) exclusivement</td>
                    <td style={cellStyle}>Sous-traitant UE</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>AWS Textract</strong> (Amazon Web Services EMEA SARL, Luxembourg)</td>
                    <td style={cellStyle}>Reconnaissance de caract&egrave;res (OCR) sur documents scann&eacute;s</td>
                    <td style={cellStyle}>eu-central-1 (Frankfurt) exclusivement</td>
                    <td style={cellStyle}>Sous-traitant UE</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>Anthropic PBC</strong> (USA)</td>
                    <td style={cellStyle}>Mod&egrave;les Claude &mdash; voie principale : AWS Bedrock UE ; voie exceptionnelle : API directe pour le skill <em>specialiste-nullites</em> (voir section 7)</td>
                    <td style={cellStyle}>Voie principale : donn&eacute;es trait&eacute;es dans AWS Bedrock EU, non transmises &agrave; Anthropic. Voie exceptionnelle : &Eacute;tats-Unis, sous consentement expr&egrave;s de l'utilisateur</td>
                    <td style={cellStyle}>Voie principale : pas de transfert direct. Voie exceptionnelle : transfert aux &Eacute;tats-Unis fond&eacute; sur l'article 49.1.a du RGPD</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>Stripe Payments Europe Ltd.</strong> (Irlande)</td>
                    <td style={cellStyle}>Traitement des paiements</td>
                    <td style={cellStyle}>UE</td>
                    <td style={cellStyle}>Sous-traitant UE</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>Google Ireland Ltd.</strong></td>
                    <td style={cellStyle}>OAuth Gmail / Google Drive / Google Calendar (activ&eacute; par l'avocat)</td>
                    <td style={cellStyle}>UE</td>
                    <td style={cellStyle}>Sous-traitant UE</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>Microsoft Ireland Operations Ltd.</strong></td>
                    <td style={cellStyle}>OAuth Outlook / OneDrive / MS Calendar (activ&eacute; par l'avocat)</td>
                    <td style={cellStyle}>UE</td>
                    <td style={cellStyle}>Sous-traitant UE</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p style={pGap}>
              La liste &agrave; jour des sous-traitants est disponible sur demande &agrave; <ExtLink href="mailto:privacy@iavocat.ai">privacy@iavocat.ai</ExtLink>. Tout changement substantiel de sous-traitant sera notifi&eacute; aux utilisateurs.
            </p>
            <p style={pGap}>
              <strong>Engagement cl&eacute; :</strong> aucun transfert de donn&eacute;es vers des sous-traitants non list&eacute;s ci-dessus. En particulier, aucune donn&eacute;e de l'avocat n'est utilis&eacute;e pour entra&icirc;ner des mod&egrave;les d'intelligence artificielle (engagement contractuel d'AWS Bedrock et d'Anthropic).
            </p>
          </section>

          {/* 7. Durées de conservation */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>6. Dur&eacute;es de conservation</h2>
            <ul style={ulStyle}>
              <li><strong>Donn&eacute;es du compte actif :</strong> tant que le compte est actif</li>
              <li><strong>Apr&egrave;s r&eacute;siliation :</strong> 30 jours (suppression imm&eacute;diate possible sur demande &agrave; <ExtLink href="mailto:privacy@iavocat.ai">privacy@iavocat.ai</ExtLink>)</li>
              <li><strong>Donn&eacute;es de facturation :</strong> 10 ans (obligation l&eacute;gale comptable)</li>
              <li><strong>Logs techniques :</strong> 12 mois maximum</li>
              <li><strong>Sauvegardes chiffr&eacute;es :</strong> 30 jours apr&egrave;s suppression des donn&eacute;es sources</li>
            </ul>
          </section>

          {/* 8. Localisation et transferts */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>7. Localisation et transferts de donn&eacute;es</h2>

            <h3 style={h3Style}>7.1 Principe g&eacute;n&eacute;ral &mdash; h&eacute;bergement et traitement dans l'Union europ&eacute;enne</h3>
            <p>
              L'ensemble des donn&eacute;es clients (dossiers, documents, correspondances, synth&egrave;ses IA) est h&eacute;berg&eacute; sur des infrastructures situ&eacute;es dans l'Union europ&eacute;enne : Supabase (AWS eu-west-1, Irlande) pour la base de donn&eacute;es et le stockage, AWS Textract eu-central-1 Frankfurt (UE) pour l'OCR, et Amazon Bedrock Frankfurt (UE) pour les traitements par intelligence artificielle Claude (Anthropic).
            </p>
            <p style={pGap}>
              My Legal Act (Dubai) n'a acc&egrave;s aux donn&eacute;es qu'&agrave; des fins d'administration et de support &mdash; acc&egrave;s logu&eacute;s et limit&eacute;s aux personnes habilit&eacute;es.
            </p>

            <h3 style={h3Style}>7.2 Exception trac&eacute;e &mdash; skill &laquo;&nbsp;specialiste-nullites&nbsp;&raquo;</h3>
            <p>
              Par exception, et en raison de contraintes techniques sp&eacute;cifiques &agrave; ce traitement particulier, le skill &laquo;&nbsp;specialiste-nullites&nbsp;&raquo; (recherche juridique avanc&eacute;e sur les nullit&eacute;s de proc&eacute;dure) peut, lorsque l'utilisateur en fait express&eacute;ment usage, effectuer des appels &agrave; l'API Anthropic h&eacute;berg&eacute;e aux &Eacute;tats-Unis.
            </p>
            <p style={pGap}>Ce traitement est strictement encadr&eacute; :</p>
            <ul style={ulStyle}>
              <li><strong>Information pr&eacute;alable :</strong> un avertissement explicite est affich&eacute; &agrave; l'utilisateur avant chaque utilisation de ce skill, pr&eacute;cisant que le traitement implique un transfert de donn&eacute;es vers les &Eacute;tats-Unis.</li>
              <li><strong>Consentement &eacute;clair&eacute; :</strong> l'utilisation du skill requiert une action affirmative de l'utilisateur apr&egrave;s lecture de cet avertissement. Le consentement est horodat&eacute; et journalis&eacute;.</li>
              <li><strong>Opt-out individuel :</strong> chaque utilisateur peut d&eacute;sactiver d&eacute;finitivement ce skill dans ses param&egrave;tres, auquel cas aucun appel vers les &Eacute;tats-Unis ne sera effectu&eacute; pour son compte.</li>
              <li><strong>Fondement juridique du transfert :</strong> article 49.1.a du RGPD &mdash; consentement explicite au transfert, l'utilisateur ayant &eacute;t&eacute; inform&eacute; des risques r&eacute;sultant de l'absence, au moment du transfert, d'une d&eacute;cision d'ad&eacute;quation et de garanties appropri&eacute;es concernant ce traitement sp&eacute;cifique.</li>
              <li><strong>Donn&eacute;es minimis&eacute;es :</strong> seules les donn&eacute;es strictement n&eacute;cessaires &agrave; la recherche juridique sont transmises (requ&ecirc;te + contexte proc&eacute;dural minimal). Aucun nom de client, aucune donn&eacute;e identifiante n'est transmise si l'utilisateur prend soin de les anonymiser.</li>
            </ul>
            <p style={pGap}>
              L'utilisateur peut &agrave; tout moment exercer ses droits d'acc&egrave;s, de rectification et d'effacement concernant les traitements op&eacute;r&eacute;s via ce skill.
            </p>

            <h3 style={h3Style}>7.3 Transferts encadr&eacute;s par Clauses Contractuelles Types</h3>
            <p>
              Les autres transferts de donn&eacute;es hors de l'Espace &eacute;conomique europ&eacute;en sont encadr&eacute;s par les Clauses Contractuelles Types adopt&eacute;es par la Commission europ&eacute;enne (d&eacute;cision 2021/914) ou par le recours &agrave; des sous-traitants &eacute;tablis dans l'UE, comme d&eacute;taill&eacute; &agrave; la section 5.
            </p>
          </section>

          {/* 9. Sécurité */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>8. S&eacute;curit&eacute; des donn&eacute;es</h2>
            <p>Nous mettons en &oelig;uvre les mesures techniques et organisationnelles suivantes :</p>
            <ul style={ulStyle}>
              <li>Chiffrement en transit (TLS 1.3)</li>
              <li>Chiffrement au repos (AES-256)</li>
              <li>Row-Level Security (RLS) activ&eacute;e sur toutes les tables sensibles Supabase &mdash; chaque avocat n'acc&egrave;de qu'&agrave; ses propres donn&eacute;es</li>
              <li>Authentification &agrave; double facteur disponible</li>
              <li>Acc&egrave;s aux donn&eacute;es limit&eacute; par RLS au seul avocat propri&eacute;taire</li>
              <li>Pas d'acc&egrave;s humain de routine aux donn&eacute;es des utilisateurs</li>
              <li>Audits de s&eacute;curit&eacute; r&eacute;guliers</li>
            </ul>
            <p style={pGap}>
              En cas de violation de donn&eacute;es, nous notifierons la CNIL dans les 72 heures conform&eacute;ment &agrave; l'article 33 du RGPD et, le cas &eacute;ch&eacute;ant, les personnes concern&eacute;es conform&eacute;ment &agrave; l'article 34 du RGPD.
            </p>
          </section>

          {/* 10. Droits des personnes */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>9. Droits des personnes (art. 12 &agrave; 22 RGPD)</h2>
            <p>Conform&eacute;ment au RGPD, vous disposez des droits suivants :</p>
            <ul style={ulStyle}>
              <li><strong>Droit d'acc&egrave;s</strong> (art. 15) : obtenir une copie de vos donn&eacute;es personnelles</li>
              <li><strong>Droit de rectification</strong> (art. 16) : corriger des donn&eacute;es inexactes ou incompl&egrave;tes</li>
              <li><strong>Droit &agrave; l'effacement</strong> (art. 17) : demander la suppression de vos donn&eacute;es (&laquo; droit &agrave; l'oubli &raquo;)</li>
              <li><strong>Droit &agrave; la limitation</strong> (art. 18) : demander que le traitement soit restreint</li>
              <li><strong>Droit &agrave; la portabilit&eacute;</strong> (art. 20) : recevoir vos donn&eacute;es dans un format structur&eacute; et lisible par machine</li>
              <li><strong>Droit d'opposition</strong> (art. 21) : vous opposer au traitement fond&eacute; sur l'int&eacute;r&ecirc;t l&eacute;gitime</li>
              <li><strong>Droit de retirer votre consentement</strong> &agrave; tout moment, sans affecter la lic&eacute;it&eacute; du traitement ant&eacute;rieur</li>
              <li><strong>Droit de d&eacute;finir des directives post mortem</strong> relatives &agrave; la conservation, l'effacement et la communication de vos donn&eacute;es apr&egrave;s votre d&eacute;c&egrave;s (art. 85 loi Informatique et Libert&eacute;s)</li>
            </ul>
            <p style={pGap}>
              <strong>Modalit&eacute;s d'exercice :</strong> adressez votre demande par email &agrave; <ExtLink href="mailto:privacy@iavocat.ai">privacy@iavocat.ai</ExtLink> en pr&eacute;cisant votre identit&eacute; et le droit exerc&eacute;. Nous r&eacute;pondrons dans un d&eacute;lai maximum d'un mois.
            </p>
            <p style={pGap}>
              <strong>R&eacute;clamation aupr&egrave;s de l'autorit&eacute; de contr&ocirc;le :</strong> si vous estimez que le traitement de vos donn&eacute;es viole vos droits, vous pouvez d&eacute;poser une r&eacute;clamation aupr&egrave;s de la CNIL :
            </p>
            <p style={pGap}>
              CNIL &mdash; 3 Place de Fontenoy, 75007 Paris &mdash; <ExtLink href="https://www.cnil.fr">www.cnil.fr</ExtLink>
            </p>
          </section>

          {/* 11. Secret professionnel */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>10. Secret professionnel de l'avocat</h2>
            <p>
              Les donn&eacute;es d&eacute;pos&eacute;es par les avocats utilisateurs sont couvertes par le secret professionnel tel que d&eacute;fini &agrave; l'article 66-5 de la loi du 31 d&eacute;cembre 1971. L'architecture technique d'IAvocat.ai est con&ccedil;ue pour respecter cette obligation fondamentale :
            </p>
            <ul style={ulStyle}>
              <li><strong>Isolation des donn&eacute;es :</strong> la Row-Level Security (RLS) de Supabase garantit qu'aucun avocat ne peut acc&eacute;der aux donn&eacute;es d'un autre</li>
              <li><strong>Chiffrement int&eacute;gral :</strong> en transit (TLS 1.3) et au repos (AES-256)</li>
              <li><strong>Pas d'acc&egrave;s humain de routine :</strong> l'&eacute;quipe technique n'acc&egrave;de aux donn&eacute;es qu'en cas de n&eacute;cessit&eacute; technique document&eacute;e et avec tra&ccedil;abilit&eacute;</li>
              <li><strong>Pas d'utilisation pour l'entra&icirc;nement IA :</strong> les donn&eacute;es des avocats ne sont jamais utilis&eacute;es pour entra&icirc;ner ou am&eacute;liorer les mod&egrave;les d'intelligence artificielle</li>
              <li><strong>H&eacute;bergement UE exclusif :</strong> toutes les donn&eacute;es restent dans l'Union europ&eacute;enne</li>
            </ul>
          </section>

          {/* 12. Cookies et traceurs */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>11. Cookies et traceurs</h2>
            <p>IAvocat.ai utilise exclusivement des cookies essentiels au fonctionnement du service :</p>
            <ul style={ulStyle}>
              <li><strong>Cookie de session Supabase :</strong> maintien de l'authentification de l'utilisateur (strictement n&eacute;cessaire)</li>
              <li><strong>Pr&eacute;f&eacute;rences d'interface :</strong> th&egrave;me, langue, &eacute;tat du panneau lat&eacute;ral (strictement n&eacute;cessaire)</li>
            </ul>
            <p style={pGap}>
              <strong>Aucun cookie tiers publicitaire n'est utilis&eacute;.</strong> Aucun traceur marketing. Aucun outil de tracking tiers (pas de Google Analytics, pas de pixels publicitaires). En l'absence de cookies non essentiels, aucun bandeau de consentement n'est requis.
            </p>
          </section>

          {/* 13. Modifications */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>12. Modifications de la pr&eacute;sente politique</h2>
            <p>
              La pr&eacute;sente politique de confidentialit&eacute; peut &ecirc;tre modifi&eacute;e pour refl&eacute;ter l'&eacute;volution de nos pratiques ou des exigences r&eacute;glementaires. Toute modification substantielle sera notifi&eacute;e par email aux utilisateurs au moins 30 jours avant son entr&eacute;e en vigueur.
            </p>
            <p style={pGap}>
              La version en vigueur est toujours accessible depuis le pied de page de la plateforme.
            </p>
          </section>

          {/* 14. Contact */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>13. Contact</h2>
            <ul style={ulStyle}>
              <li><strong>Questions RGPD :</strong> <ExtLink href="mailto:privacy@iavocat.ai">privacy@iavocat.ai</ExtLink></li>
              <li><strong>Repr&eacute;sentant UE :</strong> en cours de d&eacute;signation (voir section 1 bis)</li>
              <li><strong>Autorit&eacute; de contr&ocirc;le :</strong> CNIL &mdash; <ExtLink href="https://www.cnil.fr">www.cnil.fr</ExtLink></li>
            </ul>
          </section>

          <hr style={{ margin: '2.5rem 0', border: 'none', borderTop: `0.5px solid ${C.border}` }} />

          <p style={{ fontSize: '0.9rem', color: C.textSecondary, marginTop: '2rem' }}>
            Pour toute question concernant cette politique, contactez-nous &agrave; <ExtLink href="mailto:privacy@iavocat.ai">privacy@iavocat.ai</ExtLink>.
          </p>
        </div>
      </div>
    </div>
  )
}
